import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

type ContactRequest = {
  id: string;
  email: string;
  subject: string;
  message: string;
  lang: string;
  status: string;
  created_at: string;
};

type StatusFilter = 'all' | 'new' | 'in_progress' | 'resolved';

const STATUS_OPTIONS = [
  { value: 'new', label: '新規', labelEn: 'New', color: '#3b82f6' },
  { value: 'in_progress', label: '対応中', labelEn: 'In Progress', color: '#f59e0b' },
  { value: 'resolved', label: '解決済み', labelEn: 'Resolved', color: '#10b981' },
];

function formatJST(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status);
  const color = opt?.color || '#6b7280';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
    }}>
      {opt?.label || status}
    </span>
  );
}

export default function AdminContactsPage() {
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('contact_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setContacts(data || []);
    } catch {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    const { error: err } = await supabase
      .from('contact_requests')
      .update({ status: newStatus })
      .eq('id', id);
    if (!err) {
      setContacts(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
    }
  };

  const filtered = filter === 'all' ? contacts : contacts.filter(c => c.status === filter);
  const counts = {
    all: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    in_progress: contacts.filter(c => c.status === 'in_progress').length,
    resolved: contacts.filter(c => c.status === 'resolved').length,
  };

  const selected = contacts.find(c => c.id === selectedId) || null;

  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'すべて' },
    { key: 'new', label: '新規' },
    { key: 'in_progress', label: '対応中' },
    { key: 'resolved', label: '解決済み' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d0d', color: '#e4e8f0' }}>
      {/* Header */}
      <header style={{
        padding: '16px 24px',
        borderBottom: '1px solid #333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#1a1a1a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <a href="#/manual" style={{ color: '#a0aec0', textDecoration: 'none', fontSize: 14 }}>← マニュアルTOP</a>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>お問い合わせ管理</h1>
        </div>
        <button
          onClick={loadContacts}
          style={{
            padding: '6px 16px',
            borderRadius: 6,
            border: '1px solid #333',
            background: '#1a1a1a',
            color: '#a0aec0',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          更新
        </button>
      </header>

      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: filter === f.key ? '1px solid #01a1ff' : '1px solid #333',
                background: filter === f.key ? '#01a1ff22' : '#1a1a1a',
                color: filter === f.key ? '#01a1ff' : '#a0aec0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {f.label}
              <span style={{
                marginLeft: 6,
                padding: '1px 6px',
                borderRadius: 10,
                fontSize: 11,
                background: filter === f.key ? '#01a1ff33' : '#333',
                color: filter === f.key ? '#01a1ff' : '#a0aec0',
              }}>
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div style={{ padding: 12, marginBottom: 16, borderRadius: 6, background: '#7f1d1d33', color: '#fca5a5', border: '1px solid #7f1d1d' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#a0aec0' }}>読み込み中...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#a0aec0' }}>お問い合わせがありません</div>
        ) : (
          <div style={{ border: '1px solid #333', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#1a1a1a', borderBottom: '1px solid #333' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: '#a0aec0', fontWeight: 600 }}>ステータス</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: '#a0aec0', fontWeight: 600 }}>メール</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: '#a0aec0', fontWeight: 600 }}>件名</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: '#a0aec0', fontWeight: 600 }}>言語</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: '#a0aec0', fontWeight: 600 }}>日時</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: '#a0aec0', fontWeight: 600 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #333' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#ffffff08'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '10px 12px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</td>
                    <td style={{ padding: '10px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4, background: '#333', color: '#a0aec0' }}>{c.lang?.toUpperCase() || '-'}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#a0aec0', whiteSpace: 'nowrap', fontSize: 13 }}>{formatJST(c.created_at)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => setSelectedId(c.id)}
                        style={{
                          padding: '4px 12px',
                          borderRadius: 4,
                          border: '1px solid #333',
                          background: 'transparent',
                          color: '#01a1ff',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        詳細
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer */}
      {selected && (
        <>
          <div
            onClick={() => setSelectedId(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100 }}
          />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: 480,
            maxWidth: '100vw',
            height: '100vh',
            background: '#1a1a1a',
            borderLeft: '1px solid #333',
            zIndex: 101,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Drawer Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>お問い合わせ詳細</h2>
              <button
                onClick={() => setSelectedId(null)}
                style={{ background: 'none', border: 'none', color: '#a0aec0', fontSize: 20, cursor: 'pointer', padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* Drawer Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 4 }}>ステータス</div>
                <select
                  value={selected.status}
                  onChange={e => handleStatusChange(selected.id, e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #333',
                    background: '#0d0d0d',
                    color: '#e4e8f0',
                    fontSize: 14,
                    width: '100%',
                  }}
                >
                  {STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 4 }}>メールアドレス</div>
                <div style={{ fontSize: 14 }}>{selected.email}</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 4 }}>件名</div>
                <div style={{ fontSize: 14 }}>{selected.subject}</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 4 }}>言語</div>
                <div style={{ fontSize: 14 }}>{selected.lang?.toUpperCase() || '-'}</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 4 }}>受信日時</div>
                <div style={{ fontSize: 14 }}>{formatJST(selected.created_at)}</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 8 }}>本文</div>
                <div style={{
                  padding: 16,
                  borderRadius: 8,
                  background: '#0d0d0d',
                  border: '1px solid #333',
                  fontSize: 14,
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {selected.message}
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid #333' }}>
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '10px 16px',
                  borderRadius: 6,
                  background: '#01a1ff',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                メールで返信
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
